import { Component, type ErrorInfo, type ReactNode } from "react";
import { isChunkError, reloadOnceForNewRelease, reportError } from "@/lib/errorReporting";

interface State {
  failed: boolean;
  chunk: boolean;
}

/**
 * Last line of defence for a page that throws while rendering: report it
 * (client_error, kind "render") and offer a reload instead of a blank
 * screen. A failed lazy chunk usually means a new release went out while
 * the tab was open, so the page reloads itself once (reloadOnceForNewRelease).
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false, chunk: false };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { failed: true, chunk: isChunkError(message) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const message = error instanceof Error ? error.message : String(error);
    const component = info.componentStack?.trim().split("\n")[0]?.trim().slice(0, 80) ?? null;
    reportError("render", message, { component });
    // Code from the release before: load the page again once, by itself.
    if (isChunkError(message)) reloadOnceForNewRelease();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-xl font-semibold">
          {this.state.chunk ? "A new version of the site is available" : "Something went wrong on this page"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {this.state.chunk ? "Reload to get it." : "We've been notified. Reloading usually fixes it."}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
        >
          Reload
        </button>
      </div>
    );
  }
}
